// @ai-generated 2026-08-27T10:06:00+00:00 by malte.ulfers@tngtech.com using Claude Opus 5
import { IconFilter } from '@tabler/icons-react';
import { lookupField } from '../fieldMetadata';
import { SynqoForEachMode, type SynqoFilteredCondition, type SynqoRuleset } from '@Model';
import { ConditionCard } from './ConditionCard';
import { ConditionVisitor } from './ConditionVisitor';
import styles from './FilteredRenderer.module.css';
import { InlinePredicate } from './InlinePredicate';

interface FilteredRendererProps {
    node: SynqoFilteredCondition;
    referencedRulesets: Record<string, SynqoRuleset>;
}

/**
 * Renders a `filtered` node as one sentence — "from *collection*, where *filter*, *mode* must hold" —
 * followed by the body as a nested card.
 *
 * The filter is inlined rather than given its own card because it is restricted to a simple predicate
 * and belongs to the header sentence: it says *which* items are in scope, which the reader needs
 * before the body means anything. The body is unrestricted, so it keeps full card treatment.
 */
export function FilteredRenderer({ node, referencedRulesets }: FilteredRendererProps) {
    const collectionLabel = lookupField(node.collection.path)?.label ?? node.collection.path;
    // "zutreffend" would be redundant now that the filter itself is spelled out in the sentence.
    const modeLabel = node.mode === SynqoForEachMode.All ? 'alle' : 'mindestens einer';

    return (
        <ConditionCard accent={{ border: styles.accentBorder, icon: styles.accentIcon }}>
            <div className={styles.header}>
                <IconFilter size={16} className={`${styles.icon} ${styles.accentIcon}`} />
                <span className={styles.sentence}>
                    <span>Aus</span>
                    {/* Each comma is glued to the phrase it follows — as a standalone flex child the
                        row gap would render it as "Positionen , bei denen". */}
                    <span className={styles.clause}>
                        <span className={styles.collection}>{collectionLabel}</span>
                        <span>,</span>
                    </span>
                    <span>bei denen</span>
                    <span className={styles.clause}>
                        <InlinePredicate node={node.where} />
                        <span>,</span>
                    </span>
                    <span>müssen</span>
                    <span className={styles.mode}>{modeLabel}</span>
                    <span className={styles.trailing}>bestehen:</span>
                </span>
            </div>
            <div className={styles.body}>
                <ConditionVisitor node={node.body} referencedRulesets={referencedRulesets} />
            </div>
        </ConditionCard>
    );
}
